import React, { useState } from "react";
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Button,
} from "@mui/material";
import Iconify from "./Iconify";

export default function TicketItemModern({
  ticket,
  index,
  quantite,
  addTicket,
  removeTicket,
  // ces valeurs sont envoyées par le parent (EventPage)
  quota = 0,
  sell = 0,
  encours = 0,
  isSoldOut = false,
  showQuota = true,
}) {
  const data = ticket.data();
  const price = data.price || 0;
  const isFree = price === 0;

  const [expanded, setExpanded] = useState(false);
  const MAX_CHARS = 80;

  const used = sell + encours;
  const remaining = Math.max(quota - used, 0);
  const progress = quota > 0 ? (used / quota) * 100 : 0;

  // description à afficher (tronquée ou complète)
  const description = data.description || "";
  const isLongDesc = description.length > MAX_CHARS;
  const displayDesc =
    expanded || !isLongDesc
      ? description
      : description.slice(0, MAX_CHARS) + "…";

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid rgba(94, 23, 235, 0.08)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #FFFFFF 0%, #F7F3FF 55%, #F3EDFD 100%)",
        
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ pl: 0.5 }} // compense la barre de gauche
      >
        {/* -------- Colonne infos billet -------- */}
        <Grid item xs={12} sm={7}>
          <Stack spacing={1.2}>
            {/* titre + tags */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography fontSize={17} fontWeight={700} sx={{ color: "#111827" }}>
                {data.ticket_name}
              </Typography>

              <Chip
                size="small"
                label={isFree ? "Gratuit" : "Payant"}
                sx={{
                  fontSize: 11,
                  height: 22,
                  borderRadius: "999px",
                  bgcolor: isFree
                    ? "rgba(16, 185, 129, 0.08)"
                    : "rgba(94, 23, 235, 0.08)",
                  color: isFree ? "#059669" : "#5E17EB",
                  border: "1px solid",
                  borderColor: isFree
                    ? "rgba(16, 185, 129, 0.35)"
                    : "rgba(94, 23, 235, 0.25)",
                }}
              />
            </Stack>

            {/* description avec Voir plus / Voir moins */}
            {description && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#4B5563", whiteSpace: "pre-line" }}
                >
                  {displayDesc}
                </Typography>

                {isLongDesc && (
                  <Button
                    size="small"
                    onClick={() => setExpanded((prev) => !prev)}
                    sx={{
                      color: "#5E17EB",
                      textTransform: "none",
                      fontWeight: 500,
                      mt: 0.5,
                      px: 0,
                      "&:hover": {
                        background: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {expanded ? "Voir moins" : "Voir plus"}
                  </Button>
                )}
              </Box>
            )}

            {/* quota / stock (géré via props, donc compatible multi-séances) */}
            {showQuota && quota > 0 && (
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    {remaining > 0
                      ? `Plus que ${remaining} billet${remaining > 1 ? "s" : ""}`
                      : "Plus aucun billet disponible"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    {used}/{quota} vendus
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "rgba(148, 163, 184, 0.25)",
                    "& .MuiLinearProgress-bar": { borderRadius: 999 },
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Grid>

        {/* -------- Colonne prix + actions -------- */}
        <Grid
          item
          xs={12}
          sm={5}
          sx={{
            display: "flex",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* prix */}
          <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.5}>
            <Typography
              fontSize={13}
              sx={{
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Prix
            </Typography>
            <Typography
              fontSize={20}
              fontWeight={700}
              sx={{ color: isFree ? "#059669" : "#111827" }}
            >
              {isFree ? "Gratuit" : `${price.toLocaleString("fr-FR")} CFA`}
            </Typography>
          </Stack>

          {/* boutons quantité */}
          {isSoldOut ? (
            <Chip
              label="Épuisé"
              sx={{
                borderRadius: "999px",
                bgcolor: "#FEF3C7",
                border: "1px dashed #FACC15",
                color: "#92400E",
                fontSize: 10,
                fontWeight: 600,
              }}
            />
          ) : (
            <Box
              sx={{
                borderRadius: 999,
                bgcolor: "white",
                border: "1px solid rgba(148, 163, 184, 0.35)",
                display: "flex",
                alignItems: "center",
                // px: 1,
              }}
            >
              <Tooltip title={quantite[index] === 0 ? "" : "Enlever un billet"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={quantite[index] === 0}
                    onClick={() => removeTicket(index)}
                    sx={{
                      bgcolor: quantite[index] === 0 ? "transparent" : "#5E17EB",
                      color: quantite[index] === 0 ? "#9CA3AF" : "white",
                      "&:hover": {
                        bgcolor:
                          quantite[index] === 0 ? "transparent" : "#4A13C5",
                      },
                    }}
                  >
                    <Iconify icon="ic:baseline-minus" width={20} height={20} />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography
                fontSize={16}
                fontWeight={600}
                sx={{ px: 1.5, minWidth: 24, textAlign: "center" }}
              >
                {quantite[index]}
              </Typography>

              <Tooltip title="Ajouter un billet">
                <IconButton
                  size="small"
                  onClick={() => addTicket(index)}
                  sx={{
                    bgcolor: "#5E17EB",
                    color: "white",
                    "&:hover": { bgcolor: "#4A13C5" },
                  }}
                >
                  <Iconify icon="material-symbols:add" width={20} height={20} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Grid>
      </Grid>
    </Card>
  );
}
